"use client";

import { ApiGetFundingHistoryResponse, ApiFundingHistoryEntry } from "@cede/types";
import { API_ENDPOINT } from "@cede/utils";
import { isPerpsExchange } from "./config";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery, QueryClient } from "@tanstack/react-query";

const CEX_PERPS_FUNDING_HISTORY_QUERY_KEY = "cex-perps-funding-history";

export function invalidateCexPerpsFundingHistory(queryClient: QueryClient, exchangeId: string) {
  return queryClient.invalidateQueries({ queryKey: [CEX_PERPS_FUNDING_HISTORY_QUERY_KEY, exchangeId] });
}

interface UseCexPerpsFundingHistoryParams {
  exchangeId: string;
  accountId: number | undefined;
  symbol?: string;
  since?: number;
  limit?: number;
  enabled?: boolean;
}

const REFETCH_INTERVAL = 60 * 60_000; // 1 hour

const STALE_TIME = 60 * 60_000; // 1 hour
/**
 * Hook to fetch funding history for a CEX perps exchange
 * Exchange-agnostic: pass any supported exchangeId
 *
 * Returns the funding history entries from the SDK's getFundingHistory endpoint
 */
export function useCexPerpsFundingHistory({
  exchangeId,
  accountId,
  symbol,
  since,
  limit = 50,
  enabled = true,
}: UseCexPerpsFundingHistoryParams) {
  const { auth } = useHubUser();
  const apiHelper = useApi();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [CEX_PERPS_FUNDING_HISTORY_QUERY_KEY, exchangeId, accountId, symbol, since, limit],
    queryFn: async (): Promise<ApiGetFundingHistoryResponse> => {
      if (!auth.identityToken || !accountId) {
        return { fundingHistory: [], total: 0 };
      }

      const queryParams: Record<string, string> = {
        accountId: accountId.toString(),
      };

      if (symbol) {
        queryParams.symbol = symbol;
      }
      if (since !== undefined) {
        queryParams.since = since.toString();
      }
      if (limit !== undefined) {
        queryParams.limit = limit.toString();
      }

      const response = await apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_EXCHANGE_ACCOUNT_FUNDING_HISTORY, {
        queryParams,
      });

      return response as ApiGetFundingHistoryResponse;
    },
    enabled: enabled && isPerpsExchange(exchangeId) && Boolean(auth.identityToken) && Boolean(accountId),
    staleTime: STALE_TIME,
    refetchInterval: REFETCH_INTERVAL,
  });

  return {
    fundingHistory: data?.fundingHistory ?? [],
    total: data?.total ?? 0,
    isLoading,
    error: error as Error | null,
    refetch,
  };
}

export type { ApiFundingHistoryEntry };
